import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import DriverCard from "@/components/DriverCard";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Info } from "lucide-react";
import { useSplits } from "@/lib/useSplits";
import { useSiteSettings } from "@/context/SiteSettingsContext";

const STAT_LEGEND = [
  ["PAC", "Pace", "Schnellste Runden, Abstände zur Spitze, genereller Speed."],
  ["KON", "Konstanz", "Schwankungen in den Platzierungen über mehrere Rennen hinweg."],
  ["RAC", "Racecraft", "Gewonnene Plätze im Rennen (Differenz Start- zu Zielplatzierung)."],
  ["CLE", "Clean Racing", "Geringe Anzahl an Zeitstrafen, Verwarnungen oder Vorfällen."],
  ["QUA", "Qualifying", "Durchschnittliche Startposition und Quali-Duelle."],
  ["ERG", "Ergebnisstärke", "Durchschnittliche Zielplatzierung, Podien, Siege."],
  ["ZUV", "Zuverlässigkeit", "DNF-Quote (Ausfälle) vs. beendete Rennen."],
];

export default function DriversPage() {
  const splitsCfg = useSplits();
  const { settings } = useSiteSettings();
  const cardsHidden = !!settings?.cards_hidden;
  const SPLIT_TABS = useMemo(() => [
    { key: "all", label: "Alle", color: "#00E5FF" },
    ...splitsCfg.map(s => ({ key: s.key, label: s.name, color: s.color })),
    { key: "inactive", label: "Inaktiv", color: "#6B7280" },
  ], [splitsCfg]);
  const [drivers, setDrivers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [splitKey, setSplitKey] = useState("all");
  const [teamFilter, setTeamFilter] = useState("all");

  useEffect(() => {
    Promise.all([
      api.get("/drivers").then(x => x.data).catch(() => []),
      api.get("/teams").then(x => x.data).catch(() => []),
    ]).then(([d, t]) => { setDrivers((d || []).filter(x => !x.is_reserve_driver)); setTeams(t); });
  }, []);

  // Reset team filter when split changes
  useEffect(() => { setTeamFilter("all"); }, [splitKey]);

  const teamsById = useMemo(() => Object.fromEntries(teams.map(t => [t.id, t])), [teams]);

  // Teams that actually have drivers in the active split (only relevant for split tabs, not "all"/"inactive")
  const teamsInSplit = useMemo(() => {
    if (splitKey === "all" || splitKey === "inactive") return [];
    const ids = new Set(
      drivers
        .filter(d => d.is_active !== false && (d.split || "champions") === splitKey)
        .map(d => d.team_id)
        .filter(Boolean)
    );
    return teams.filter(t => ids.has(t.id));
  }, [drivers, teams, splitKey]);

  const visible = (key) => {
    let list;
    if (key === "inactive") {
      list = drivers.filter(d => d.is_active === false);
    } else if (key === "all") {
      // "Alle" zeigt nur aktive Fahrer; Inaktive haben einen eigenen Tab
      list = drivers.filter(d => d.is_active !== false);
    } else {
      list = drivers.filter(d => d.is_active !== false && (d.split || "champions") === key);
      if (teamFilter !== "all") list = list.filter(d => d.team_id === teamFilter);
    }
    return [...list].sort((a, b) => {
      // Rookies (ohne Wertung) immer ans Ende
      const ar = a.is_rookie ? 1 : 0;
      const br = b.is_rookie ? 1 : 0;
      if (ar !== br) return ar - br;
      return (b.ovr || 0) - (a.ovr || 0) || (b.points || 0) - (a.points || 0);
    });
  };

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-10 py-14" data-testid="drivers-page">
      <div className="h-1 w-12 bg-[#00E5FF] mb-4" />
      <h1 className="font-display font-black italic uppercase tracking-normal text-5xl sm:text-7xl leading-none">Fahrer</h1>
      <div className="flex items-center gap-3 mt-3 max-w-2xl">
        <p className="text-neutral-400">Hier findet ihr die Fahrerkarten unserer Piloten.</p>
        <Popover>
          <PopoverTrigger asChild>
            <button
              type="button"
              data-testid="stats-info-btn"
              className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest font-bold border border-white/15 px-2.5 py-1 hover:border-[#00E5FF] hover:text-[#00E5FF] transition-colors"
            >
              <Info className="w-3.5 h-3.5" /> Werte erklärt
            </button>
          </PopoverTrigger>
          <PopoverContent
            align="start"
            className="w-80 bg-[#0F0F0F] border-white/15 text-white p-0"
            data-testid="stats-info-popover"
          >
            <div className="p-4 border-b border-white/10">
              <div className="eyebrow text-[#00E5FF]">Bedeutung der Werte</div>
              <div className="font-display font-black italic uppercase text-lg mt-1">Karten-Stats</div>
            </div>
            <ul className="p-4 space-y-2.5">
              {STAT_LEGEND.map(([code, name, desc]) => (
                <li key={code} className="flex items-start gap-3">
                  <span className="font-display font-black italic text-[#00E5FF] text-base w-10 shrink-0 leading-none">{code}</span>
                  <div>
                    <div className="text-sm font-bold leading-tight">{name}</div>
                    <div className="text-xs text-neutral-400 mt-0.5">{desc}</div>
                  </div>
                </li>
              ))}
            </ul>
          </PopoverContent>
        </Popover>
      </div>

      <Tabs value={splitKey} onValueChange={setSplitKey} className="mt-10">
        <TabsList className="bg-transparent border border-white/10 p-0 rounded-none">
          {SPLIT_TABS.map(s => (
            <TabsTrigger key={s.key} value={s.key} data-testid={`drivers-tab-${s.key}`}
              className="data-[state=active]:bg-[#00E5FF] data-[state=active]:text-black uppercase tracking-widest font-bold rounded-none px-6 py-2 text-xs">
              {s.label}
            </TabsTrigger>
          ))}
        </TabsList>

        {splitKey !== "all" && splitKey !== "inactive" && teamsInSplit.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-6" data-testid="team-filter-row">
            <button
              onClick={() => setTeamFilter("all")}
              data-testid="team-filter-all"
              className={`text-xs font-bold uppercase tracking-widest px-4 py-2 transition-colors border ${teamFilter === "all" ? "border-[#00E5FF] text-[#00E5FF]" : "border-white/10 text-neutral-300 hover:border-white/30"}`}
            >
              Alle Teams
            </button>
            {teamsInSplit.map((t) => {
              const active = teamFilter === t.id;
              return (
                <button
                  key={t.id}
                  onClick={() => setTeamFilter(t.id)}
                  data-testid={`team-filter-${t.id}`}
                  className={`text-xs font-bold uppercase tracking-widest px-4 py-2 transition-colors border inline-flex items-center gap-2 ${active ? "" : "border-white/10 text-neutral-300 hover:border-white/30"}`}
                  style={active ? { borderColor: t.color, color: t.color } : {}}
                >
                  {t.badge_url ? (
                    <img src={t.badge_url} alt="" className="w-4 h-4 object-contain" />
                  ) : (
                    <span className="inline-block w-2 h-3.5" style={{ background: t.color }} />
                  )}
                  {t.short_name} · {t.name}
                </button>
              );
            })}
          </div>
        )}

        {SPLIT_TABS.map(s => {
          const list = visible(s.key);
          return (
            <TabsContent key={s.key} value={s.key} className="mt-8">
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-y-10 gap-x-6">
                {list.map((d, idx) => (
                  <div key={d.id} className="relative">
                    <div className="absolute -top-3 -left-2 z-30 font-display font-black italic text-3xl leading-none text-[#00E5FF] pointer-events-none"
                         style={{ textShadow: "0 2px 8px rgba(0,0,0,0.6)" }} data-testid={`driver-rank-${d.id}`}>
                      #{idx + 1}
                    </div>
                    <DriverCard driver={d} team={teamsById[d.team_id]} teamsById={teamsById} testId={`drivers-card-${d.id}`} hidden={cardsHidden && d.is_active !== false} />
                  </div>
                ))}
                {list.length === 0 && <div className="text-neutral-500">Keine Fahrer.</div>}
              </div>
            </TabsContent>
          );
        })}
      </Tabs>
    </div>
  );
}
